import { useMemo } from "react";
import { MarkerF as GoogleMapsMarker, MarkerProps as GoogleMapsMarkerProps, } from "@react-google-maps/api";
import { defaultMapMarkerColor } from "../../../../theme/colors";
import * as CSS from "csstype";

export const getDefaultMarkerIcon = (color: string | CSS.Property.Color = defaultMapMarkerColor) => {
  return `data:image/svg+xml,%3Csvg%20xmlns=%22http://www.w3.org/2000/svg%22%20height=%2236px%22%20viewBox=%220%20-960%20960%20960%22%20width=%2236px%22%20fill=%22${color.replace("#", "%23")}%22%3E%3Cpath%20d=%22M480-480q33%200%2056.5-23.5T560-560q0-33-23.5-56.5T480-640q-33%200-56.5%2023.5T400-560q0%2033%2023.5%2056.5T480-480Zm0%20400Q319-217%20239.5-334.5T160-552q0-150%2096.5-239T480-880q127%200%20223.5%2089T800-552q0%20100-79.5%20217.5T480-80Z%22/%3E%3C/svg%3E`
}

export type MarkerIcon = string | google.maps.Icon | google.maps.Symbol;

const badgedIcons = new Map<string, google.maps.Icon>();

/**
 * The default pin with a count badge at its top right, e.g. a site's loads ready; red when `alert`. Cached, so a
 * marker re-rendered with the same badge keeps the same icon and is not reset.
 */
export const getBadgedMarkerIcon = (
  color: string | CSS.Property.Color = defaultMapMarkerColor,
  count: number,
  alert = false,
): google.maps.Icon => {
  const key = `${color}|${count}|${alert}`;
  const cached = badgedIcons.get(key);
  if (cached) return cached;
  const text = count > 99 ? "99+" : String(count);
  const badgeWidth = text.length > 2 ? 28 : 22;
  // The badge's center sits over the pin's right shoulder; the image widens to fit it.
  const badgeX = 34;
  const width = badgeX + badgeWidth / 2 + 1;
  const pin = decodeURIComponent(getDefaultMarkerIcon(color).replace("data:image/svg+xml,", ""))
    .replace("<svg ", '<svg x="0" y="8" ');
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="44" viewBox="0 0 ${width} 44">`
    + pin
    + `<rect x="${badgeX - badgeWidth / 2}" y="1" width="${badgeWidth}" height="18" rx="9" fill="${alert ? "#dc2626" : "#15803d"}" stroke="#ffffff" stroke-width="2"/>`
    + `<text x="${badgeX}" y="14" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="11" font-weight="700" fill="#ffffff">${text}</text>`
    + `</svg>`;
  const icon = {
    url: `data:image/svg+xml,${encodeURIComponent(svg)}`,
    // The pin's tip, as the plain pin anchors: centered under it at the bottom.
    anchor: new google.maps.Point(18, 44),
    scaledSize: new google.maps.Size(width, 44),
  };
  badgedIcons.set(key, icon);
  return icon;
};

export type MarkerProps = Omit<GoogleMapsMarkerProps, "icon"> & {
  icon?: MarkerIcon
}

export const Marker = (props: MarkerProps) => {
  const { icon, options, ...rest } = props;
  // MarkerF reads the icon prop only when it creates the marker; options are reapplied on every change, so an icon
  // that changes later (a badge arriving, a unit going idle) travels there.
  const markerOptions = useMemo(
    () => icon === undefined ? options : {...options, icon},
    [icon, options]
  );
  return <GoogleMapsMarker {...rest} icon={icon} options={markerOptions} />;
};
